import React, { useState, useEffect } from 'react';
import ShippingEstimationItem from './ShippingEstimationItem.component';
import { ValidationErrors } from '../Validations';

import UI from '../../ui';

const { WrappedButton, WrappedButtonType, WrappedTooltip } = UI;

const areEqual = (prevProps, nextProps) => true;

const ShippingEstimation = React.memo(({ isMobile, inboundId, updateInstructions }) => {
	const [isValid, setIsValid] = useState(true);
	const [boxesValidation, setBoxesValidation] = useState([
		{
			isWeightInvalid: false,
			isDimensionsInvalid: false,
			weightErrorLabel: '',
			dimensionsErrorLabel: '',
		},
	]);
	const [boxesList, setBoxesList] = useState([
		{
			pounds: 0,
			ounces: 0,
			length: 0,
			width: 0,
			height: 0,
		},
	]);

	const addNewBoxItem = () => {
		const newBoxesList = [...boxesList];
		const newBoxesValidation = boxesValidation;

		newBoxesList.push({
			pounds: 0,
			ounces: 0,
			length: 0,
			width: 0,
			height: 0,
		});

		setBoxesList(newBoxesList);

		newBoxesValidation.push({
			isWeightInvalid: false,
			isDimensionsInvalid: false,
			weightErrorLabel: '',
			dimensionsErrorLabel: '',
		});

		setBoxesValidation(newBoxesValidation);
	};

	useEffect(() => {
		if (boxesList.length === 0) {
			addNewBoxItem();
		}
	}, [boxesList]);

	useEffect(() => {
		setBoxesList([
			{
				pounds: 0,
				ounces: 0,
				length: 0,
				width: 0,
				height: 0,
			},
		]);
	}, [inboundId]);

	const [estimatedAmount, setEstimatedAmount] = useState('$-.--'); // Todo: Using static value but needs to be updated based on rates returned from api

	const validateBoxItems = (boxItemList, updatedIndex) => {
		const newBoxesValidation = boxesValidation;
		let valid = true;

		boxItemList.forEach((boxItem, index) => {
			const totalWeightOunces = boxItem.pounds * 16 + boxItem.ounces;
			const totalSize = boxItem.length + boxItem.width * 2 + boxItem.height * 2;
			const boxItemValidation = {
				isWeightInvalid: false,
				isDimensionsInvalid: false,
				weightErrorLabel: '',
				dimensionsErrorLabel: '',
			};

			if (totalWeightOunces > 150 * 16) {
				boxItemValidation.isWeightInvalid = true;
				boxItemValidation.weightErrorLabel = ValidationErrors.weightMax;
				valid = false;
			} else if (totalWeightOunces === 0) {
				boxItemValidation.isWeightInvalid = true;
				boxItemValidation.weightErrorLabel = ValidationErrors.weightZero;
				valid = false;
			} else {
				boxItemValidation.isWeightInvalid = false;
			}

			if (boxItem.length > 108) {
				boxItemValidation.isDimensionsInvalid = true;
				boxItemValidation.dimensionsErrorLabel = ValidationErrors.lengthMax;
				valid = false;
			} else if (totalSize > 165) {
				boxItemValidation.isDimensionsInvalid = true;
				boxItemValidation.dimensionsErrorLabel = ValidationErrors.dimensionsMax;
				valid = false;
			} else {
				boxItemValidation.isDimensionsInvalid = false;
			}

			if (index === updatedIndex) {
				newBoxesValidation[index] = boxItemValidation;
			}
		});

		setIsValid(valid);
		setBoxesValidation(newBoxesValidation);
	};

	const onBoxItemChange = (updatedState, index) => {
		const newBoxesList = [...boxesList];
		const boxItem = newBoxesList[index];

		newBoxesList[index] = { ...boxItem, ...updatedState };
		setBoxesList(newBoxesList);
		validateBoxItems(newBoxesList, index);

		if (isValid) {
			// TODO - make call to get estimated cost of boxes
			setEstimatedAmount('$5.53');
		} else {
			setEstimatedAmount('$-.--');
		}
	};

	const cloneBoxItem = index => {
		const newBoxesList = [...boxesList];
		const newBoxesValidation = boxesValidation;

		newBoxesList.push({ ...newBoxesList[index] });
		setBoxesList(newBoxesList);

		newBoxesValidation.push({ ...newBoxesValidation[index] });

		setBoxesValidation(newBoxesValidation);
	};

	const onDeleteButtonClick = index => {
		const newBoxesList = [...boxesList];
		const newBoxesValidation = boxesValidation;

		newBoxesList.splice(index, 1);
		setBoxesList(newBoxesList);

		newBoxesValidation.splice(index, 1);
		setBoxesValidation(newBoxesValidation);
	};

	const mobileClassName = isMobile ? 'mobile-wrapper' : '';

	return (
		<div className={`common-inbound-details-shipping-details-estimation ${mobileClassName}`}>
			<div className="shipping-estimation-content">
				<div className="boxes-content">
					<div className="p-grid p-col-12 p-dir-col">
						{!isMobile && (
							<div className="p-grid p-dir-row sub-header">
								<p className="p-md-2 p-lg-2">Box</p>
								<p className="p-md-3 p-lg-3">Weight</p>
								<p className="p-md-4 p-lg-4">Dimensions</p>
							</div>
						)}

						{boxesList.length > 0 &&
							boxesList.map((boxItem, index) => (
								<ShippingEstimationItem
									key={`index${index}`}
									isMobile={isMobile}
									onActionTrigger={onBoxItemChange}
									boxItemIndex={index}
									boxItem={boxItem}
									cloneBoxItem={cloneBoxItem}
									onDeleteButtonClick={onDeleteButtonClick}
									updateInstructions={updateInstructions}
									boxesValidation={boxesValidation}
								/>
							))}
					</div>

					<WrappedButton
						buttonType={WrappedButtonType.Icon}
						label="Add another box"
						icon="add-icon"
						className="add-box-button"
						onClick={addNewBoxItem}
						dataqa="shipping-estimation-add-button"
					/>
				</div>
			</div>

			<div className="estimated-cost-wrapper">
				<div className="estimated-cost">
					<span>Estimated Total: {estimatedAmount}</span>
				</div>
				<WrappedTooltip
					tooltip="This estimated total is based on the weight and dimensions of your shipments, the Warehouse you’re shipping to, and your Ship From address."
					target="estimated-cost"
					position="top"
				/>
			</div>
		</div>
	);
}, areEqual);

ShippingEstimation.displayName = 'ShippingEstimation';

export default ShippingEstimation;
